import { RolePermissions } from '@common/user-permissions/rolePermissions';
import {
  selectFeatureFlags,
  selectRolePermissions,
} from '@common/user-permissions/selectors';
import { v4 as uuid } from 'uuid';
import { find, findIndex, findLast, includes } from 'lodash';
import { all, fork, put, select, takeEvery } from 'typed-redux-saga/macro';

import { enqueueSnackbar } from '@common-modules/snackbar';
import { getActiveState } from '@redact-components/MediaDetailsPage/FacesWrapperTab/ClustersResultsTab/utils';
import { processEngineRequestAction } from '../../engines/optical-tracking/actions';
import { eq, scaleBoundingPolyObject } from '@utils';

import { KEYBOARD_EVENT, KeyboardEventPayload } from '@redact-modules/keyboard';
import { selectRouteType } from '@common-modules/routing';
import {
  DETECTION_TYPES,
  OVERLAY_PREVIEW_OPTIONS,
  PLAYBACK_RATES,
} from '@helpers/constants';
import { componentActions } from '@redact-components/MediaDetailsPage/Editor/MediaPlayer/reduxHelpers/componentActions';
import { sagaIntl } from '@i18n';

import {
  actionChangeOverlay,
  actionJumpVideo,
  actionOverlayPreview,
  actionPauseVideo,
  actionPlaybackRateVideo,
  actionPlayVideo,
  actionSeekVideo,
  actionSetSelectedGroups,
  actionTogglePlayPauseVideo,
  FACE_HIGHLIGHT,
  FETCH_UPDATE_ASSET_ACTION,
  REDACT_TDO,
  deleteDetectionOverlayAction,
  deleteDetectionGroupAction,
  deleteDetectionInFrameAction,
  deleteUDROverlayAction,
  deleteUDRInFrameAction,
  deleteUDRGroupAction,
  SET_PREV_MARKER_TIME,
  UNDO_ACTION,
  REDO_ACTION,
  actionPlayVideoBackwards,
  actionOverlayTool,
} from '../actions';
import {
  selectCurrentPosition,
  selectHighlightedOverlay,
  // selectHighlightedClusterItemGroup,
  selectHighlightedClusterItem,
  selectHighlightedPoly,
  selectMediaDuration,
  selectOverlayPreview,
  selectPolysAtCurrentTime,
  selectPrevMarkerTime,
  selectSelected,
  selectShowNonRedactedOverlays,
  selectGettingRedactionJobStatus,
  selectContentTypeHasImage,
  selectIsRedactionInFlight,
  selectIsSaveRunning,
  selectOverlayTool,
} from '../selectors';
import { ProcessEngineRequest } from '../../engines/optical-tracking/models/services';
import { arrayHasContents } from '@common/shared/util';
import { ACTION_TYPE } from '@redact/components/MediaDetailsPage/Editor/types';

interface Action {
  type: string;
  payload: KeyboardEventPayload;
}

const isControlPressed = (e: KeyboardEventPayload) => e.ctrlKey || e.metaKey;
const isAltPressed = (e: KeyboardEventPayload) => e.altKey;
const isModifierPressed = (e: KeyboardEventPayload) =>
  e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;
const isShiftPressed = (e: KeyboardEventPayload) => e.shiftKey;

export function* keyboardSaga() {
  yield* all([fork(actionKeyboardEvent)]);
}

/**
 * This saga takes the keyboard actions and sends them to the
 * appropriate handlers.
 */
export function* actionKeyboardEvent() {
  const controlPlayheadHandler = controlPlayhead();
  const increasePolyFn = scaleBoundingPolyObject(5);
  const decreasePolyFn = scaleBoundingPolyObject(-4.76);

  yield* takeEvery(
    KEYBOARD_EVENT,
    actionKeyboardEventHandle({
      controlPlayheadHandler,
      increasePolyFn,
      decreasePolyFn,
    })
  );
}

export let keyDown = false;

export function* checkRolePermission(role: keyof RolePermissions) {
  const rolePermissions = yield* select(selectRolePermissions);
  return rolePermissions[role] ?? false;
}

export const actionKeyboardEventHandle = ({
  controlPlayheadHandler,
  increasePolyFn,
  decreasePolyFn,
}: {
  controlPlayheadHandler: ReturnType<typeof controlPlayhead>;
  increasePolyFn: Parameters<typeof changePolySize>[0];
  decreasePolyFn: Parameters<typeof changePolySize>[0];
}) =>
  function* ({ payload }: { payload: KeyboardEventPayload }) {
    const routeType = yield* select(selectRouteType);
    const editEnabled = yield* checkRolePermission('keyboardEditShortcuts');
    if (
      routeType === 'route/ROUTE_MEDIA_DETAILS' ||
      routeType === 'route/ROUTE_MEDIA_EDITOR'
    ) {
      if (payload.type === 'keyup') {
        switch (payload.key) {
          case 'q':
          case 'p':
            yield* toggleOverlayPreview();
            break;
          case 'w':
            if (!isModifierPressed(payload)) {
              yield* changePlaybackRate(1);
            }
            if (isControlPressed(payload)) {
              yield* changePlaybackRate(1, true);
            }
            break;
          case 'W':
            yield* jumpToNextSegment();
            break;
          case 'r':
            keyDown = false;
            break;
          case 's':
            if (!isModifierPressed(payload)) {
              yield* changePlaybackRate(-1);
            }
            if (isControlPressed(payload)) {
              yield* changePlaybackRate(-1, true);
            }
            keyDown = false;
            break;
          case 'e':
            yield* put(actionPlaybackRateVideo({ playbackRate: 1 }));
            break;
          case 'S':
            yield* selectNextPoly();
            break;
          case 't':
            if (!editEnabled) {
              return;
            }
            yield* releaseTextOverlayTool();
            break;
          case 'T': {
            if (!editEnabled) {
              return;
            }
            const trackEnabled = yield* checkRolePermission('showTrackOption');
            if (!trackEnabled) {
              break;
            }
            yield* trackObject();
            break;
          }
          case 'x':
            if (!editEnabled) {
              return;
            }
            yield* toggleHighlightedClusterItem();
            break;
          case 'X':
            if (!editEnabled) {
              return;
            }
            yield* toggleHighlightedSegment();
            break;
          case ' ':
            if (isShiftPressed(payload)) {
              yield* setPrevMarkerTime();
            } else if (isControlPressed(payload)) {
              yield* gotoPrevMarkerTime();
            } else {
              yield* togglePlayPause();
            }
            keyDown = false;
            break;
          case 'Backspace':
          case 'Delete':
            if (!editEnabled) {
              return;
            }
            if (isControlPressed(payload)) {
              yield* deletePolyCluster();
            } else if (isShiftPressed(payload)) {
              yield* deletePolyInFrame();
            } else {
              yield* deletePoly();
            }
            break;
          case '+':
            if (!editEnabled) {
              return;
            }
            yield* changePolySize(increasePolyFn); // UDRs only
            break;
          case '_':
            if (!editEnabled) {
              return;
            }
            yield* changePolySize(decreasePolyFn); // UDRs only
            break;
        }
      } else if (payload.type === 'keydown') {
        switch (payload.key) {
          case 't':
            if (!editEnabled) {
              return;
            }
            if (!isModifierPressed(payload)) {
              yield* engageTextOverlayTool();
            }
            break;
          case 'z':
            if (!editEnabled) {
              return;
            }
            if (isControlPressed(payload)) {
              if (isShiftPressed(payload)) {
                yield* put(REDO_ACTION());
              } else {
                yield* put(UNDO_ACTION());
              }
            }
            break;
          case 'Z':
            if (!editEnabled) {
              return;
            }
            if (isControlPressed(payload)) {
              yield* put(REDO_ACTION());
            }
            break;
          case 's':
          case 'ß' /* macOS */:
            if (!editEnabled) {
              return;
            }
            if (isAltPressed(payload) && !keyDown) {
              yield saveData();
              keyDown = true;
            }
            break;
          case 'r':
          case '®' /* macOS */:
            if (!editEnabled) {
              return;
            }
            if (isAltPressed(payload) && !keyDown) {
              yield* redactFile();
              keyDown = true;
            }
            break;
        }
      }
      yield* controlPlayheadHandler(payload);
    }
  };

export const gotoPrevMarkerTime = function* () {
  const prevMarkerTime = yield* select(selectPrevMarkerTime);
  if (prevMarkerTime) {
    yield* put(actionSeekVideo({ startTimeMs: prevMarkerTime }));
  }
};

export const setPrevMarkerTime = function* () {
  const currentTime = yield* select(selectCurrentPosition);
  yield* put(SET_PREV_MARKER_TIME(currentTime));
};

export const jumpToNextSegment = function* () {
  const highlightedOverlay = yield* select(selectHighlightedOverlay);

  if (highlightedOverlay) {
    const timeMs = highlightedOverlay.timeMs;

    // since only support merge cluster view right now use all cluster segments
    const cluster = yield* select(selectHighlightedClusterItem);
    const segments = cluster?.segments;

    // if we support split view would switch to this
    // const group = yield* select(selectHighlightedClusterItemGroup);
    // const segments = group?.segments;

    if (segments) {
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (
          segment &&
          segment.startTimeMs <= timeMs &&
          segment.stopTimeMs > timeMs &&
          segment.groupId === highlightedOverlay.groupId
        ) {
          i = (i + 1) % segments.length;
          const nextSegment = segments[i]!; // Safe based on limits
          const startTimeMs = nextSegment.startTimeMs;
          const firstObjectId = nextSegment.objectIds[0];
          if (firstObjectId) {
            yield* put(
              FACE_HIGHLIGHT({
                id: firstObjectId,
                type: nextSegment.type,
                groupId: nextSegment.groupId,
                timeMs: startTimeMs,
              })
            );
          }
          yield* put(actionSeekVideo({ startTimeMs }));
          return;
        }
      }
    }
  }
};

export const selectNextPoly = function* () {
  let polys = yield* select(selectPolysAtCurrentTime);
  const selectedPolys = yield* select(selectSelected);
  const showNonRedactedOverlays = yield* select(selectShowNonRedactedOverlays);
  if (!showNonRedactedOverlays) {
    polys = polys.filter((p) => !!selectedPolys[p.subsegmentId]);
  }
  const hiPoly = yield* select(selectHighlightedPoly);
  if (arrayHasContents(polys)) {
    let poly = polys[0];

    if (hiPoly) {
      const index = polys.findIndex(eq(hiPoly));
      poly = polys[index + 1] || poly;
    }
    yield* put(
      FACE_HIGHLIGHT({
        id: poly.id,
        type: poly.type,
        groupId: poly.groupId,
        timeMs: poly.startTimeMs,
      })
    );
  }
};

export const togglePlayPause = function* () {
  yield* put(actionTogglePlayPauseVideo());
};

export const deletePoly = function* () {
  const hiPoly = yield* select(selectHighlightedPoly);

  if (!hiPoly) {
    return;
  }

  if (includes(DETECTION_TYPES, hiPoly.type)) {
    yield* put(deleteDetectionOverlayAction(hiPoly));
  } else {
    yield* put(deleteUDROverlayAction(hiPoly));
  }
};

export const deletePolyCluster = function* () {
  const hiPoly = yield* select(selectHighlightedPoly);

  if (!hiPoly) {
    return;
  }

  if (includes(DETECTION_TYPES, hiPoly.type)) {
    yield* put(deleteDetectionGroupAction(hiPoly));
  } else {
    yield* put(deleteUDRGroupAction(hiPoly));
  }
};

export const deletePolyInFrame = function* () {
  const hiPoly = yield* select(selectHighlightedPoly);
  const currentTime = yield* select(selectCurrentPosition);

  if (!hiPoly) {
    return;
  }

  if (includes(DETECTION_TYPES, hiPoly.type)) {
    yield* put(deleteDetectionInFrameAction(hiPoly, currentTime));
  } else {
    yield* put(deleteUDRInFrameAction(hiPoly, currentTime));
  }
};

export const changePolySize = function* (
  fn: ReturnType<typeof scaleBoundingPolyObject>
) {
  const hiPoly = yield* select(selectHighlightedPoly);
  if (hiPoly?.type === 'udr') {
    const {
      object: { boundingPoly },
    } = fn({
      object: {
        boundingPoly: [...hiPoly.boundingPoly],
      },
    });
    yield* put(
      actionChangeOverlay({
        id: hiPoly.id,
        type: hiPoly.type,
        groupId: hiPoly.groupId,
        boundingPoly,
        actionType: ACTION_TYPE.RESIZE,
      })
    );
  }
};

// Spring-loaded text tool: holding "t" engages the text tool and releasing it
// restores whatever tool was active before, so a stray tap can't leave the
// editor stuck in text mode.
let textToolHeld = false;
let toolBeforeTextHold: 'redaction' | 'text' = 'redaction';
if (typeof window !== 'undefined') {
  // A keyup lost to a focus change (alt-tab mid-hold) would otherwise leave the
  // hold engaged forever, and engageTextOverlayTool early-returns while it is
  // set — making every later hold a silent no-op. Clearing the flag restores
  // the shortcut; the tool itself is restored by the next release or a toolbar
  // click (dispatching from here would need a saga channel for window blur).
  window.addEventListener('blur', () => {
    textToolHeld = false;
  });
}

export const engageTextOverlayTool = function* () {
  if (textToolHeld) {
    // keydown auto-repeats while held; only act on the first one.
    return;
  }
  // Org-level kill switch (enableTextOverlay app config set to false): with
  // the tool hidden, its shortcut must not engage either. Release stays
  // unguarded — it no-ops unless a hold engaged (textToolHeld), and must still
  // restore the previous tool if the flag flips mid-hold.
  const { enableTextOverlay } = yield* select(selectFeatureFlags);
  if (!enableTextOverlay) {
    return;
  }
  const overlayTool = yield* select(selectOverlayTool);
  toolBeforeTextHold = overlayTool;
  textToolHeld = true;
  if (overlayTool !== 'text') {
    yield* put(actionOverlayTool('text'));
  }
};

export const releaseTextOverlayTool = function* () {
  if (!textToolHeld) {
    return;
  }
  textToolHeld = false;
  const overlayTool = yield* select(selectOverlayTool);
  if (overlayTool !== toolBeforeTextHold) {
    yield* put(actionOverlayTool(toolBeforeTextHold));
  }
};

export const trackObject = function* () {
  const hiPoly = yield* select(selectHighlightedPoly);
  if (hiPoly?.type === 'udr') {
    yield* put(
      enqueueSnackbar({
        message: 'Starting the optical tracking process.',
      })
    );
    const payload: ProcessEngineRequest = {
      id: uuid().toString(),
      overlay: hiPoly.id,
      boundingPoly: hiPoly.boundingPoly,
      timeMs: hiPoly.startTimeMs,
      parentUdrId: hiPoly.groupId,
      trackBack: true,
      trackForward: true,
    };
    yield* put(processEngineRequestAction(payload));
  }
};

export const redactFile = function* () {
  const gettingRedactionJobStatus = yield* select(
    selectGettingRedactionJobStatus
  );
  // The two guards the Redact File button carries, which this shortcut is
  // otherwise a way around (VE-27738). A burn-in already in flight would be
  // raced by a second run — the image path exports in the browser, so two runs
  // derive the same version from the same pre-upload TDO and both stamp it. A
  // save still uploading is worse than it looks: `tdoIsChanged` is cleared when
  // the save STARTS, so the run reads the editor as clean and labels the asset
  // that save is about to supersede.
  const isRedactionInFlight = yield* select(selectIsRedactionInFlight);
  const isSaveRunning = yield* select(selectIsSaveRunning);
  if (!gettingRedactionJobStatus && !isRedactionInFlight && !isSaveRunning) {
    yield* put(REDACT_TDO());
  }
};

export const saveData = function* () {
  yield* put(
    enqueueSnackbar({
      message: 'Saving your changes.',
    })
  );
  yield* put(FETCH_UPDATE_ASSET_ACTION());
};

export const toggleOverlayPreview = function* () {
  const overlayPreview = yield* select(selectOverlayPreview);
  const hasImage = yield* select(selectContentTypeHasImage);

  const previewOptions = hasImage
    ? OVERLAY_PREVIEW_OPTIONS.filter((option) => option.value !== 'black_fill')
    : OVERLAY_PREVIEW_OPTIONS;

  const i = findIndex(previewOptions, { value: overlayPreview });
  yield* put(
    actionOverlayPreview(
      // Index action is safe due to module with length
      previewOptions[(i + 1) % previewOptions.length]!.value
    )
  );
};

export const changePlaybackRate = function* (dir: 1 | -1, integral = false) {
  const playbackSpeed = yield* select(
    (s: { player: { playbackRate: number } }) => s.player.playbackRate
  );
  const playbackRate =
    dir === 1
      ? find(
          PLAYBACK_RATES,
          (v) => v > playbackSpeed && (!integral || Number.isInteger(v))
        ) || PLAYBACK_RATES.at(-1)
      : findLast(
          PLAYBACK_RATES,
          (v) => v < playbackSpeed && (!integral || Number.isInteger(v))
        ) || PLAYBACK_RATES[0];
  if (playbackRate) {
    yield* put(actionPlaybackRateVideo({ playbackRate: playbackRate }));
  }
};

/**
 * Selects/unselects the highlighted cluster.
 */
// export const toggleHighlightedClusterItem = function* () {
//   const selected = yield* select(selectSelected);
//   const highlightedOverlayCluster = yield* select(selectHighlightedOverlayCluster);

//   if (highlightedOverlayCluster) {
//     yield* put(
//       actionSetSelectedGroups({
//         selected: highlightedOverlayCluster.segments.reduce((a, ss) => {
//           const newSelected = ss.subsegmentIds.reduce<{
//             [key: string]: boolean;
//           }>((total, current) => {
//             total[current] =
//               getActiveState(highlightedOverlayCluster, selected) === -1;
//             return total;
//           }, {});

//           Object.assign(a, newSelected);
//           return a;
//         }, {}),
//       })
//     );
//   }
// };

/**
 * Selects/unselects the highlighted cluster
 */
export const toggleHighlightedClusterItem = function* () {
  const intl = sagaIntl();
  const selected = yield* select(selectSelected);
  const highlightedClusterItem = yield* select(selectHighlightedClusterItem);

  if (!highlightedClusterItem) {
    return;
  }
  const currentActiveState = getActiveState(
    highlightedClusterItem.segments,
    selected
  );
  const active = currentActiveState !== 1;
  const isMergedGroup = highlightedClusterItem.isMergedGroup;

  const actionName = active
    ? intl.formatMessage({
        id: 'redactLowerCase',
        defaultMessage: 'redact',
      })
    : intl.formatMessage({
        id: 'unredactLowerCase',
        defaultMessage: 'unredact',
      });

  // localized object-type label (e.g. head -> "Head"), falling back to the raw type
  const clusterTypeLabel = intl.messages[highlightedClusterItem.type];
  const clusterType =
    typeof clusterTypeLabel === 'string'
      ? clusterTypeLabel
      : highlightedClusterItem.type;

  const description = isMergedGroup
    ? `${actionName} ${intl.formatMessage({ id: 'mergedGroupHistoryText', defaultMessage: 'merged group' })}`
    : `${actionName} ${clusterType} ${intl.formatMessage({ id: 'groupHistoryText', defaultMessage: 'group' })}`;

  yield* put(
    actionSetSelectedGroups({
      selected: highlightedClusterItem.segments.reduce((a, ss) => {
        const newSelected = ss.subsegmentIds.reduce<{
          [key: string]: boolean;
        }>((total, current) => {
          total[current] =
            getActiveState(highlightedClusterItem.segments, selected) === -1;
          return total;
        }, {});

        Object.assign(a, newSelected);
        return a;
      }, {}),
      groupIds: [
        ...new Set(highlightedClusterItem.segments.map((s) => s.groupId)),
      ],
      description,
    })
  );
};

/**
 * Selects/Unselects the highlighted segment in the cluster
 */
export const toggleHighlightedSegment = function* () {
  const selected = yield* select(selectSelected);
  const highlightedOverlay = yield* select(selectHighlightedOverlay);
  const highlightedClusterItem = yield* select(selectHighlightedClusterItem);

  if (!highlightedOverlay || !highlightedClusterItem) {
    return;
  }

  const segment = highlightedClusterItem.segments.find(
    (s) =>
      s.groupId === highlightedOverlay.groupId &&
      s.startTimeMs <= highlightedOverlay.timeMs &&
      s.stopTimeMs >= highlightedOverlay.timeMs
  );

  if (!segment) {
    return;
  }

  const active = !!selected[segment.id];
  const intl = sagaIntl();

  const actionName = !active
    ? intl.formatMessage({ id: 'redactLowerCase', defaultMessage: 'redact' })
    : intl.formatMessage({
        id: 'unredactLowerCase',
        defaultMessage: 'unredact',
      });

  // localized object-type label (e.g. head -> "Head"), falling back to the raw type
  const segmentTypeLabel = intl.messages[segment.type];
  const segmentType =
    typeof segmentTypeLabel === 'string' ? segmentTypeLabel : segment.type;
  const segmentText = intl.formatMessage({
    id: 'segmentHistoryText',
    defaultMessage: 'segment',
  });

  const description = `${actionName} ${segmentType} ${segmentText}`;

  yield* put(
    actionSetSelectedGroups({
      selected: segment.subsegmentIds.reduce<{ [key: string]: boolean }>(
        (acc, subsegmentId) => {
          acc[subsegmentId] = !active;
          return acc;
        },
        {}
      ),
      groupIds: [segment.groupId],
      description,
    })
  );
};

/**
 * Use l/r arrow, a/d keys to step through playhead.
 */
export function controlPlayhead() {
  const keysOfInterest = ['arrowright', 'arrowleft', 'a', 'd'];
  let downCount = 0;
  let lastKey: string | undefined = undefined;
  let isPlaying = false;
  return function* (e: Action['payload']) {
    const key = e.key.toLowerCase();

    // reset downCount if key changes to prevent accidental hold down detection if different keysOfInterest are pressed in sequence
    // reset if not a keydown
    if (lastKey !== key || e.type !== 'keydown') {
      downCount = 0;
    }
    lastKey = key;

    if (keysOfInterest.includes(key)) {
      downCount += e.type === 'keydown' ? 1 : 0;
    }

    const isShiftPressed = e.shiftKey;
    const isAltPressed = e.altKey;
    const isCtrlPressed = isControlPressed(e);

    if (
      e.type === 'keydown' &&
      isShiftPressed &&
      !isAltPressed &&
      !isCtrlPressed
    ) {
      if (key === 'arrowup') {
        const minWindowMs = 0;
        yield* put(actionSeekVideo({ startTimeMs: minWindowMs }));
        yield* put(componentActions.onUpdateCurrentTime(minWindowMs));
        return;
      }
      if (key === 'arrowdown') {
        yield* put(actionSeekVideo({ startTimeMs: Infinity }));
        return;
      }
    }

    if (e.type === 'keydown' && downCount === 2) {
      if (key === 'arrowright' || key === 'd') {
        isPlaying = true;
        yield* put(actionPlayVideo());
        return;
      }

      // holding down left arrow or A should use backwards emulation
      if (key === 'arrowleft' || key === 'a') {
        isPlaying = true;
        yield* put(actionPlayVideoBackwards());
        return;
      }
    }

    let jumpFrames = 1; // default jump size is 1 frame

    // keep legacy ctrl + l/r arrow behavior - likely will reassign to something more useful
    if (isCtrlPressed) {
      jumpFrames = 10;
    }

    // use modifiers to jump 1 sec or 10 sec
    let jumpSecs = 0.0;

    if (isShiftPressed) {
      jumpSecs = 1; // jump 1 sec
    }
    if (isAltPressed && isShiftPressed) {
      jumpSecs = 10; // jump 10 sec
    }
    if (isCtrlPressed && isShiftPressed) {
      const duration = yield* select(selectMediaDuration);
      if (duration < 60) {
        jumpSecs = duration; // jump duration sec
      } else {
        jumpSecs = 60; // jump 60 sec
      }
    }

    // cancel emulation mode anytime keyup
    if (isPlaying && e.type === 'keyup') {
      isPlaying = false;
      downCount = 0;
      yield* put(actionPauseVideo()); // cancel forwards/backwards playing
      return;
    }

    if (key === 'arrowleft' || key === 'a') {
      if (e.type === 'keyup') {
        if (jumpSecs) {
          yield* put(actionJumpVideo({ seconds: -jumpSecs }));
        } else {
          yield* put(actionJumpVideo({ frames: -jumpFrames }));
        }
      }
    } else if (key === 'arrowright' || key === 'd') {
      if (e.type === 'keyup') {
        if (jumpSecs) {
          yield* put(actionJumpVideo({ seconds: jumpSecs }));
        } else {
          yield* put(actionJumpVideo({ frames: jumpFrames }));
        }
      }
    }
  };
}
